<script setup>
// @ts-nocheck
import { onMounted, onUnmounted, ref, watch, nextTick, defineAsyncComponent, computed } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSessionStorage } from '@vueuse/core'
import NavBar from './components/NavBar.vue'
import Footer from './components/Footer.vue'
import ToastNotification from './components/ToastNotification.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'
import BetaBanner from './components/BetaBanner.vue'
import CompareBar from './components/CompareBar.vue'
import { useTheme } from './composables/useTheme'
import { useAccentColor } from './composables/useAccentColor'
import { fetchCSRFToken } from './services/api'
import { detectBrowserLanguage } from './utils/languageUtils'
import { useUserStore } from './stores/userStore'
import { useAuthStore } from './stores/authStore'
import { useToastStore } from './stores/toastStore'
import logger from './utils/logger'

const { locale, t } = useI18n({ useScope: 'global' })
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const authStore = useAuthStore()
const toastStore = useToastStore()

const CookieConsent = defineAsyncComponent(() => 
  import('./components/CookieConsent.vue')
)

// Festival-related routes where compare bar should be shown
const isFestivalRoute = computed(() => {
  const path = route.path
  return path.startsWith('/festivals') || path.startsWith('/festival') || path === '/festivals/compare'
})

// Delay loading heavy modals until after page is interactive
const { isDark } = useTheme()
const { accentColor, setAccentColor, initAccentColor } = useAccentColor()

// Initialize theme and accent color on mount
onMounted(() => {
  initAccentColor(isDark.value)
})

// Reapply accent color when theme changes (to use correct light/dark palette)
watch(isDark, () => {
  setAccentColor(accentColor.value, isDark.value)
})

watch(() => userStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
    authStore.setActive()
  }
})

const showCookieDetails = ref(false)
const cookieDetailsRef = ref(null)
const closeButtonRef = ref(null)
const isPageInteractive = ref(false)
const isFooterAdDismissed = useSessionStorage('footer_ad_dismissed', false)
const isBetaBannerDismissed = useSessionStorage('beta_banner_dismissed', false)

// Store timer references for cleanup
let pageInteractiveTimer = null
let geolocationTimer = null

const dismissFooterAd = () => {
  isFooterAdDismissed.value = true
}

const dismissBetaBanner = () => {
  isBetaBannerDismissed.value = true
}

// Delay loading modals until page is interactive (improves FCP/LCP)
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => { isPageInteractive.value = true }, { timeout: 2000 })
  } else {
    pageInteractiveTimer = setTimeout(() => { isPageInteractive.value = true }, 2000)
  }
}

const handleCookieKeydown = (event) => {
  if (event.key === 'Escape' && showCookieDetails.value) {
    showCookieDetails.value = false
  }
  // Focus trap for cookie details modal
  if (showCookieDetails.value && event.key === 'Tab') {
    const focusableElements = cookieDetailsRef.value?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusableElements?.length) {
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }
}

const handleCookieDetailsOpen = async () => {
  showCookieDetails.value = true
  await nextTick()
  closeButtonRef.value?.focus()
}

// Global keyboard shortcuts
const handleGlobalKeydown = (event) => {
  // Ignore if user is typing in an input
  const target = event.target
  const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
  
  // "/" to open search
  if (event.key === '/' && !isInput) {
    event.preventDefault()
    const searchInput = document.querySelector('[data-search-input]')
    searchInput?.focus()
  }
  
  // "Escape" to close any open modals (if not already handled)
  if (event.key === 'Escape') {
    // Emit event for other components to handle
    window.dispatchEvent(new CustomEvent('global:escape'))
  }
}

watch(() => authStore.sessionState, async (state) => {
  if (state === 'expired' && userStore.isLoggedIn) {
    const currentRoute = router.currentRoute.value
    await userStore.logout(false)
    if (currentRoute.meta?.requiresAuth) {
      toastStore.warning(t('auth.session_expired'), 7000, 'session-expired')
      router.push({ name: 'login', query: { redirect: currentRoute.fullPath } })
    }
  }
})

const handleVisibilityChange = async () => {
  if (document.visibilityState !== 'visible') return
  // Skip during OAuth callback — initUser is called explicitly there with force=true
  if (router.currentRoute.value.name === 'login-callback') return
  if (userStore.isLoggedIn) {
    const isValid = await userStore.initUser()
    if (!isValid && userStore.isLoggedIn) {
      authStore.setExpired()
    }
  }
}

onMounted(async () => {
  window.addEventListener('keydown', handleCookieKeydown)
  window.addEventListener('keydown', handleGlobalKeydown)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Fetch CSRF token on app start
  await fetchCSRFToken()

  // Browser/OS language detection for new users
  if (!localStorage.getItem('user-locale')) {
    const detectedLang = detectBrowserLanguage(['en', 'es', 'ca', 'gl', 'eu'])
    
    if (detectedLang !== locale.value) {
      logger.info(`Auto-detected language from browser/OS: ${detectedLang}`)
      locale.value = detectedLang
      localStorage.setItem('user-locale', detectedLang)
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleCookieKeydown)
  window.removeEventListener('keydown', handleGlobalKeydown)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (pageInteractiveTimer) clearTimeout(pageInteractiveTimer)
  if (geolocationTimer) clearTimeout(geolocationTimer)
})
</script>

<template>
  <ErrorBoundary>
    <div
      class="min-h-screen bg-brand-background text-brand-text font-sans selection:bg-brand-accent selection:text-white flex flex-col">
      
      <!-- Skip link for keyboard users -->
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand-accent focus:text-white focus:rounded-lg focus:font-medium"
      >
        {{ $t('common.skip_to_content') }}
      </a>

      <!-- Beta Banner -->
      <BetaBanner
        v-if="!isBetaBannerDismissed"
        @close="dismissBetaBanner"
      />

      <NavBar />
      
    <main id="main-content" class="flex-grow" tabindex="-1">
      <!-- page-fade-slide: opacity + translateY only (GPU composite path).
           mode="out-in" ensures the leaving view fully exits before the
           entering view renders, preventing z-index / overlap artefacts.
           :key="route.path" forces a new transition on every route change.
           Vue Router 4 requires slot props pattern with <component :is="Component" /> -->
      <RouterView v-slot="{ Component }">
        <Transition name="page-fade-slide" mode="out-in">
          <component :is="Component" :key="route.path" />
        </Transition>
      </RouterView>
    </main>
      
      <!-- Compare Bar - only show on festival-related routes -->
      <CompareBar v-if="isFestivalRoute" />
      
      <Footer />
      
      <!-- Footer Ad - Dismissible -->
      <div 
        v-if="!isFooterAdDismissed"
        class="w-full h-20 bg-brand-surface/30 flex items-center justify-center border-t border-brand-muted/10 relative"
      >
        <span class="text-brand-muted text-sm">{{ $t('ads.advertisement') }}</span>
        <button
          type="button"
          :aria-label="t('ads.dismiss')"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-brand-muted hover:text-brand-text transition-colors"
          @click="dismissFooterAd"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
      
      <!-- Notifications -->
      <ToastNotification />

      <!-- Cookie Consent - Lazy loaded after page is interactive -->
      <CookieConsent v-if="isPageInteractive" @show-details="handleCookieDetailsOpen" />
      
      <!-- Cookie Details Modal -->
      <div
        v-if="showCookieDetails"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-details-title"
      >
        <button
          type="button"
          class="absolute inset-0"
          :aria-label="t('common.close_details')"
          @click="showCookieDetails = false"
        ></button>
        <div
          ref="cookieDetailsRef"
          class="bg-brand-surface rounded-lg max-w-lg w-full p-6 shadow-xl"
          role="document"
          @click.stop
        >
          <h2 id="cookie-details-title" class="text-xl font-bold text-brand-text mb-4">{{ $t('cookies.info_title') }}</h2>
          <div class="space-y-4 text-sm text-brand-muted">
            <p>
              {{ $t('cookies.essential_desc') }}
            </p>
            <ul class="list-disc list-inside space-y-2">
              <li>
                <strong class="text-brand-text">{{ $t('cookies.csrf_name') }}</strong> - 
                {{ $t('cookies.csrf_desc') }}
              </li>
              <li>
                <strong class="text-brand-text">{{ $t('cookies.refresh_name') }}</strong> - 
                {{ $t('cookies.refresh_desc') }}
              </li>
              <li>
                <strong class="text-brand-text">{{ $t('cookies.session_name') }}</strong> - 
                {{ $t('cookies.session_desc') }}
              </li>
            </ul>
            <p class="text-xs text-brand-muted/70 mt-4">
              {{ $t('cookies.footer_desc') }}
            </p>
          </div>
          <button
            ref="closeButtonRef"
            @click="showCookieDetails = false"
            class="mt-6 w-full px-4 py-2 bg-brand-accent text-white font-medium rounded-lg hover:bg-brand-accent/90 transition-colors"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  </ErrorBoundary>
</template>
